import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { InvoiceDetailEntity } from './invoice-detail.entity';
import { ItemEntity } from './item.entity';
import { OrderEntity } from './order.entity';
import { ShipmentDetailEntity } from './shipment-detail.entity';

@Index('order_detail__fk_item__id_fk', ['fkItemId'], {})
@Index(
	'order_detail__fk_order__id__deleted_userId',
	['fkOrderId', 'deletedUserId'],
	{},
)
@Index('order_detail_pk_2', ['externalId'], { unique: true })
@Index(
	'order_detail_itemExternalId_deletedDate',
	['itemExternalId', 'deletedDate'],
	{},
)
@Entity('order_detail')
export class OrderDetailEntity extends BaseEntity {
	@Column('varchar', {
		name: 'externalId',
		nullable: true,
		unique: true,
		length: 64,
	})
	public externalId!: string | null;

	@Column('varchar', { name: 'itemExternalId', nullable: true, length: 64 })
	public itemExternalId!: string | null;

	@Column('int', { name: '_fk_order__id', unsigned: true })
	public fkOrderId!: number;

	@Column('int', { name: '_fk_item__id', nullable: true, unsigned: true })
	public fkItemId!: number | null;

	@Column('int', { name: 'lineNumber', nullable: true })
	public lineNumber!: number | null;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('int', { name: 'quantityOrdered' })
	public quantityOrdered!: number;

	@Column('int', { name: 'quantityShipped', nullable: true })
	public quantityShipped!: number | null;

	@Column('decimal', { name: 'price', nullable: true, precision: 15, scale: 6 })
	public price!: string | null;

	@Column('decimal', {
		name: 'minimumPrice',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public minimumPrice!: string | null;

	@Column('decimal', {
		name: 'listPrice',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public listPrice!: string | null;

	@Column('decimal', {
		name: 'subtotal',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public subtotal!: string | null;

	@Column('decimal', {
		name: 'freight',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freight!: string | null;

	@Column('decimal', { name: 'tax', nullable: true, precision: 13, scale: 2 })
	public tax!: string | null;

	@Column('decimal', {
		name: 'discount',
		nullable: true,
		precision: 21,
		scale: 10,
	})
	public discount!: string | null;

	@Column('varchar', { name: 'discountType', length: 32, default: () => "'$'" })
	public discountType!: string;

	@Column('decimal', { name: 'total', nullable: true, precision: 13, scale: 2 })
	public total!: string | null;

	@Column('decimal', {
		name: 'acquisitionCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public acquisitionCost!: string | null;

	// Snapshot of `acquisitionCost` taken before any vendor-funded
	// promo cost adjustment is written back. The promo engine
	// always reads cost from this column (with a COALESCE fallback
	// to `acquisitionCost` for legacy orders that pre-date the
	// snapshot). Lazy-populated on first apply: if NULL when the
	// engine runs, we copy the current `acquisitionCost` into it
	// before computing any adjustment. Never modified after that
	// initial snapshot, so the engine is idempotent across re-runs.
	@Column('decimal', {
		name: 'acquisitionCostOriginal',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public acquisitionCostOriginal!: string | null;

	@Column('decimal', {
		name: 'sellCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public sellCost!: string | null;

	// Same lazy-snapshot semantics as `acquisitionCostOriginal`,
	// applied to `sellCost`. Vendor-funded promos reduce both per
	// unit by the same dollar amount; this column preserves the
	// pre-adjustment value so we can recompute idempotently.
	@Column('decimal', {
		name: 'sellCostOriginal',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public sellCostOriginal!: string | null;

	@Column('decimal', {
		name: 'totalGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGp!: string | null;

	@Column('decimal', {
		name: 'gpMargin',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public gpMargin!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('smallint', {
		name: 'lostOpportunityQuantity',
		nullable: true,
		unsigned: true,
	})
	public lostOpportunityQuantity!: number | null;

	@Column('int', { name: 'quantityBackordered', nullable: true })
	public quantityBackordered!: number | null;

	@Column('decimal', {
		name: 'conversionFactor',
		precision: 11,
		scale: 4,
		default: () => "'1.0000'",
	})
	public conversionFactor!: string;

	// When set, this OrderDetail line is a "phantom" Get-item auto-added
	// (or customer-selected) by the promo engine for a BXGY/free-gift
	// promo. Engine treats these lines specially: they are NOT eligible
	// for additional promo math, and if the parent promo's BUY conditions
	// stop qualifying, the line stays in cart at full price with a UI
	// warning until the customer re-qualifies or removes it.
	@Column('int', {
		name: 'fk_promo__id',
		nullable: true,
		unsigned: true,
	})
	public fkPromoId!: number | null;

	// Merchandiser destination for this line. Set by the dispatch
	// wizard when an auto-generated refill order spans multiple
	// merchandisers for a single customer: each (item × merchandiser)
	// tuple is its own order_detail row, so the driver can see at the
	// stop "this Coke line is for Cooler A; that one is for Cooler B".
	//
	// Purely informational. We do NOT write to merchandiser
	// `location_item.quantity` on delivery — sensors are the source of
	// truth for merchandiser fill levels, and the next order-gen run
	// reads `fillPct` to drive the next refill. This FK exists so the
	// driver can organize their loadout and the post-delivery summary
	// can show "expected per cooler" for the manager / customer.
	//
	// NULL means "no specific merchandiser destination" — covers
	// non-refill orders (manual customer orders), legacy data before
	// this column existed, and the rare case where the wizard couldn't
	// attribute the line to a single merchandiser.
	@Column('int', {
		name: 'fk_location__id_merchandiser',
		nullable: true,
		unsigned: true,
	})
	public fkLocationIdMerchandiser!: number | null;

	// The price contract whose matching item row supplied each value on this
	// line, stamped at the time the value was resolved. A separate FK per value
	// because price, acquisition cost, and sell cost can each be driven by a
	// different contract (price matching honors the client's contractPricingRule;
	// cost matching uses priority tiebreakers — see PriceContractsService).
	// NULL means no contract drove that value — it came from the item's own
	// list price / cost, a manual override, or a saved-price/promo path.
	@Column('int', {
		name: 'fk_price_contract__id_price',
		nullable: true,
		unsigned: true,
	})
	public fkPriceContractIdPrice!: number | null;

	@Column('int', {
		name: 'fk_price_contract__id_acquisitionCost',
		nullable: true,
		unsigned: true,
	})
	public fkPriceContractIdAcquisitionCost!: number | null;

	@Column('int', {
		name: 'fk_price_contract__id_sellCost',
		nullable: true,
		unsigned: true,
	})
	public fkPriceContractIdSellCost!: number | null;

	@ManyToOne(() => OrderEntity, (order) => order.orderDetails, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_order__id', referencedColumnName: 'id' }])
	public order!: OrderEntity;

	@ManyToOne(() => ItemEntity, (item) => item.orderDetails, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_item__id', referencedColumnName: 'id' }])
	public item!: ItemEntity;

	@OneToMany(
		() => InvoiceDetailEntity,
		(invoiceDetail) => invoiceDetail.orderDetail,
		{},
	)
	public invoiceDetails!: Array<InvoiceDetailEntity>;

	@OneToMany(
		() => ShipmentDetailEntity,
		(shipmentDetail) => shipmentDetail.orderDetail,
		{},
	)
	public shipmentDetails!: Array<ShipmentDetailEntity>;
}
